import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as api from "../lib/api.js";

const PositionsContext = createContext(null);
export const isSwingPosition = (position) => !position?.strategy || position.strategy === "calls";
/** Raised by the daily job and not yet sold or ignored. */
export const isPendingExit = (p) => p.status === "open" && p.exitDue && p.alert && !p.alert.ignored;

/**
 * The ledger, shared by the page that lists it and the scanner that adds to it.
 *
 * Both need to know which symbols are already held — the scanner to stop
 * offering a second "I bought this" on a name you hold, the page to value it —
 * so the list is fetched once here rather than by each of them.
 */
export function PositionsProvider({ children }) {
  const [positions, setPositions] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await api.getPositions();
      setPositions(data.positions ?? []);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const add = useCallback(async (input) => {
    const { position } = await api.addPosition(input);
    setPositions((current) => [position, ...(current ?? [])]);
    return position;
  }, []);

  const close = useCallback(async (id, exit) => {
    const { position } = await api.closePosition(id, exit);
    setPositions((current) => (current ?? []).map((p) => (p.id === id ? position : p)));
    return position;
  }, []);

  const update = useCallback(async (id, input) => {
    const { position } = await api.updatePosition(id, input);
    setPositions((current) => (current ?? []).map((p) => (p.id === id ? { ...position, alert: p.alert } : p)));
    return position;
  }, []);

  const ignoreAlert = useCallback(async (id) => {
    const { alert } = await api.ignoreExitAlert(id);
    setPositions((current) => (current ?? []).map((p) => (p.id === id ? { ...p, alert } : p)));
  }, []);

  const remove = useCallback(async (id) => {
    await api.removePosition(id);
    setPositions((current) => (current ?? []).filter((p) => p.id !== id));
  }, []);

  const openSymbols = useMemo(
    () => new Set((positions ?? []).filter((p) => p.status === "open").map((p) => p.symbol)),
    [positions],
  );

  const pendingExits = useMemo(() => (positions ?? []).filter(isPendingExit), [positions]);

  const value = useMemo(
    () => ({ positions, error, refresh, add, update, close, remove, ignoreAlert, openSymbols, pendingExits }),
    [positions, error, refresh, add, update, close, remove, ignoreAlert, openSymbols, pendingExits],
  );

  return <PositionsContext.Provider value={value}>{children}</PositionsContext.Provider>;
}

export function usePositions() {
  return useContext(PositionsContext) ?? {
    positions: null, error: null, openSymbols: new Set(), pendingExits: [],
    refresh: async () => {}, add: async () => {}, update: async () => {}, close: async () => {}, remove: async () => {}, ignoreAlert: async () => {},
  };
}
